import axios from "axios";
import { appConfig } from "../config/config";

const isBrowser = typeof window !== "undefined";

const axiosInstance = axios.create({
  baseURL: appConfig.apiBaseUrl,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

const refreshAccessToken = async () => {
  if (!isBrowser) return null;

  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      localStorage.clear();
      // DEF-178: chat/session state lives in sessionStorage and survives
      // localStorage.clear(); drop it on forced logout too.
      try { sessionStorage.clear(); } catch { /* non-fatal */ }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
        window.location.href = "/auth/sign-in";
      }
      return null;
    }

    const response = await axios.post(
      `${appConfig.apiBaseUrl}/api/v1/auth/refresh-token`,
      {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    const { access_token, refresh_token } = response.data;
    if (access_token && refresh_token) {
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      return access_token;
    }

    return null;
  } catch (error) {
    localStorage.clear();
    // DEF-178: chat/session state lives in sessionStorage and survives
    // localStorage.clear(); drop it on forced logout too.
    try { sessionStorage.clear(); } catch { /* non-fatal */ }
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
      window.location.href = "/auth/sign-in";
    }
    return null;
  }
};

axiosInstance.interceptors.request.use(
  (config) => {
    if (isBrowser) {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!isBrowser) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (originalRequest?.url && originalRequest.url.includes("/api/v1/auth/")) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const newToken = await refreshAccessToken();

      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }
    }

    if (error.response?.status === 401) {
      localStorage.clear();
      // DEF-178: chat/session state lives in sessionStorage and survives
      // localStorage.clear(); drop it on forced logout too.
      try { sessionStorage.clear(); } catch { /* non-fatal */ }
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/auth")) {
        window.location.href = "/auth/sign-in";
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
